// src/pages/OpenSourceNotices.jsx
// Third-party software notices: a curated page over an internal, complete
// inventory.
//
// The page names only the components worth naming — the scientific modelling
// stack, the principal application libraries, and the solver/rendering packages
// in the server image. No versions anywhere, no inline licence texts (dialog on
// demand), no copyleft flagging: those either invite questions that have no
// customer-facing answer or hand a competitor a reconnaissance document (stack,
// exact versions, CVE surface) for free.
//
// The JSON this page fetches (public/third-party-notices.json) is the TRIMMED
// file written by the generator's --public-out: exactly the curated components
// named below, plus the server image packages. The complete dependency closure —
// every package, version and licence text — lives in
// pythia-backend/docs/third-party-notices.full.json and is NOT published; the
// page states instead that additional attribution information is maintained by
// MetEn Solutions and available upon request, which is the standard commercial
// posture. Keep CORE_LIBS/APP_LIBS below in sync with PUBLIC_COMPONENTS in
// pythia-backend/scripts/generate_third_party_notices.py.
//
// REGENERATE INSIDE THE DEPLOYED IMAGE where possible: run outside it and the
// backend section reports the local environment's packages rather than the
// deployed ones. Versions are no longer published, which removes the sharpest
// edge of that, but a package absent locally still drops off the page entirely.
import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

// Kept inline as a guaranteed baseline: the pvlib wheel does not ship a LICENSE
// file in its dist-info, so the generator cannot recover this text, and pvlib is
// the one component whose licence should be shown even if the inventory fails to
// load. BSD-3-Clause requires the notice, conditions and disclaimer be reproduced.
const PVLIB_LICENSE = `BSD 3-Clause License

Copyright (c) 2023 pvlib python Contributors
Copyright (c) 2014 PVLIB python Development Team
Copyright (c) 2013 Sandia National Laboratories

All rights reserved.

Redistribution and use in source and binary forms, with or without modification,
are permitted provided that the following conditions are met:

  Redistributions of source code must retain the above copyright notice, this
  list of conditions and the following disclaimer.

  Redistributions in binary form must reproduce the above copyright notice, this
  list of conditions and the following disclaimer in the documentation and/or
  other materials provided with the distribution.

  Neither the name of the copyright holder nor the names of its
  contributors may be used to endorse or promote products derived from
  this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND
ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED
WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE
DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT HOLDER OR CONTRIBUTORS BE LIABLE FOR
ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES
(INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES;
LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON
ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT
(INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS
SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.`;

// --------------------------------------------------------------------------
// Curated lists. `key` must match the package name in third-party-notices.json
// (backend uses importlib names, frontend uses npm names). A key that is not
// found in the inventory is silently skipped, so a renamed or dropped package
// cannot break the page — it just stops being listed until this file is
// updated. Keep in sync with PUBLIC_COMPONENTS in the generator script.
// --------------------------------------------------------------------------

// Named deliberately, with a description: a technical reviewer who recognises
// pvlib or PySAM reads them as validated, citable implementations, and the Role
// column tells them what each one actually does here.
//
// The page states this positively and stops there. It does not contrast these
// libraries with our own code — see the note above the section text.
const CORE_LIBS = [
  {
    key: "pvlib",
    label: "pvlib-python",
    note: "Photovoltaic system modelling and solar position/irradiance calculations. The reference implementation maintained with Sandia National Laboratories.",
  },
  {
    key: "NREL-PySAM",
    label: "PySAM (NREL)",
    note: "NREL System Advisor Model bindings, used for battery performance and degradation.",
  },
  {
    key: "pyomo",
    label: "Pyomo",
    note: "Optimisation modelling layer for the dispatch problem.",
  },
  {
    key: "highspy",
    label: "HiGHS",
    note: "High-performance linear and mixed-integer solver.",
  },
  {
    key: "windpowerlib",
    label: "windpowerlib",
    note: "Wind turbine power curve modelling and wind speed extrapolation.",
  },
  {
    key: "solarfactors",
    label: "solarfactors",
    note: "Bifacial and view-factor irradiance modelling.",
  },
  {
    key: "entsoe-py",
    label: "entsoe-py",
    note: "ENTSO-E Transparency Platform client for European market and grid data.",
  },
];

// The four frameworks PYTHIA is built on — one per tier, and no further.
//
// Deliberately short. A reader evaluating the platform cares which scientific
// methods produce the numbers (the section above) and, at most, what the
// application is architected on. Recharts, date-fns, Uvicorn, Pydantic and the
// numerical stack are implementation detail: listing them adds length without
// adding anything a customer can act on, and none of it is distributed in a way
// that obliges us to name it here. The complete closure — every component, direct
// and transitive, with licence texts — remains in the downloadable inventory.
const APP_LIBS = [
  { key: "react", label: "React" },
  { key: "@mui/material", label: "Material UI" },
  { key: "fastapi", label: "FastAPI" },
  { key: "sqlalchemy", label: "SQLAlchemy" },
];

const MONO = {
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
  fontSize: "0.72rem",
  whiteSpace: "pre-wrap",
  lineHeight: 1.55,
};

const NOTICES_URL = `${process.env.PUBLIC_URL || ""}/third-party-notices.json`;
// Served as a static asset, not fetched or parsed here — the link opens it
// directly. Generated by scripts/generate_third_party_notices.py
// --attribution-out in pythia-backend.
const ATTRIBUTION_URL = `${process.env.PUBLIC_URL || ""}/third-party-attribution.txt`;

// Full licence text on request, not inline: a dialog keeps the page readable
// while the text itself stays one click away. pvlib falls back to the baked-in
// text above; anything else without a shipped text points at the project page.
function LicenceDialog({ pkg, onClose }) {
  if (!pkg) return null;
  const text = pkg.license_text || (pkg.key === "pvlib" ? PVLIB_LICENSE : null);
  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        <Typography component="span" translate="no" data-no-translate sx={{ fontWeight: 700 }}>
          {pkg.label}
        </Typography>
        {/* Licence identifier only. The published inventory carries no version
            field: it would report whichever environment the generator ran in,
            which is not necessarily the deployed one. */}
        <Typography component="span" variant="body2" sx={{ color: "text.secondary", ml: 1 }}>
          {pkg.license}
        </Typography>
        <IconButton
          aria-label="Close"
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {text ? (
          <>
            {/* Some packages omit their licence file from the published tarball.
                Those texts were retrieved from the project's own repository, and
                the source is stated rather than left to look as though it
                shipped with the package. */}
            {pkg.license_text_source && pkg.license_text_source !== "package" && (
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", display: "block", mb: 1 }}
              >
                Not included in the published package; retrieved from{" "}
                {pkg.license_text_source}
              </Typography>
            )}
            <Box sx={MONO}>{text}</Box>
          </>
        ) : (
          /* Neutral wording on purpose. Many projects publish only the licence
             identifier in package metadata and keep the text in their repository —
             that is normal practice, not an error by them or a failure here, and
             the copy should not read as though something went wrong. */
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              Licence text not bundled
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.75 }}>
              This package's licence text is not distributed within the installed
              package. The official licence is available on the{" "}
              {pkg.url ? (
                <Link href={pkg.url} target="_blank" rel="noreferrer noopener">
                  project's website
                </Link>
              ) : (
                "project's website"
              )}
              . MetEn Solutions maintains a copy for compliance purposes and can
              provide it upon request.
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

// No version column by design — see the header comment. Versions are not
// published at all; they live only in the internal inventory.
function ComponentTable({ rows, withNotes, onShowLicence }) {
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Component</TableCell>
          <TableCell>Licence</TableCell>
          {withNotes && <TableCell>Role</TableCell>}
          <TableCell align="right" />
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((p) => (
          <TableRow key={p.key} hover>
            <TableCell translate="no" data-no-translate sx={{ fontWeight: 600 }}>{p.label}</TableCell>
            {/* nowrap: the column is narrow enough that "BSD-3-Clause" otherwise
                breaks at its hyphens and renders as "BSD-3-" / "Clause". */}
            <TableCell sx={{ whiteSpace: "nowrap" }}>{p.license || "—"}</TableCell>
            {withNotes && (
              <TableCell>
                <Typography variant="caption" color="text.secondary">
                  {p.note}
                </Typography>
              </TableCell>
            )}
            <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
              <Stack direction="row" spacing={1.5} justifyContent="flex-end">
                {p.url && (
                  <Link
                    href={p.url}
                    target="_blank"
                    rel="noreferrer noopener"
                    variant="caption"
                    sx={{ fontWeight: 700 }}
                  >
                    Project
                  </Link>
                )}
                <Link
                  component="button"
                  type="button"
                  variant="caption"
                  onClick={() => onShowLicence(p)}
                  sx={{ fontWeight: 700 }}
                >
                  Licence
                </Link>
              </Stack>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export default function OpenSourceNotices() {
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const [licencePkg, setLicencePkg] = useState(null);

  useEffect(() => {
    let alive = true;
    fetch(NOTICES_URL)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((j) => alive && setData(j))
      .catch(() => alive && setFailed(true));
    return () => {
      alive = false;
    };
  }, []);

  // One lookup across both halves of the inventory: the curated lists mix npm
  // and Python packages, and the reader does not care which side a name is on.
  const index = useMemo(() => {
    const m = new Map();
    for (const section of ["frontend", "backend"]) {
      for (const p of data?.[section] || []) {
        if (!m.has(p.name)) m.set(p.name, p);
      }
    }
    return m;
  }, [data]);

  const resolve = (list) =>
    list
      .map((c) => {
        const p = index.get(c.key);
        return p ? { ...p, ...c } : null;
      })
      .filter(Boolean);

  const coreRows = useMemo(() => resolve(CORE_LIBS), [index]); // eslint-disable-line react-hooks/exhaustive-deps
  const appRows = useMemo(() => resolve(APP_LIBS), [index]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box sx={{ maxWidth: 1180, mx: "auto", px: { xs: 1, md: 2 }, py: { xs: 2, md: 3 } }}>
      <Paper
        elevation={3}
        sx={{ p: { xs: 2, md: 3 }, borderRadius: 2.5, border: "1px solid", borderColor: "divider" }}
      >
        <Stack spacing={2.5}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: "0.02em" }}>
              Third-Party Software Notices
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.75 }}>
              PYTHIA incorporates selected open-source software components.
              Attribution and licence information is provided below in accordance
              with the applicable licences. Additional compliance documentation is
              maintained by MetEn Solutions and is available upon request for
              customers, auditors and licensing enquiries.
            </Typography>
            {data?.generated_date && (
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", display: "block", mt: 0.5 }}
              >
                {/* "updated", not "reviewed": the date tracks when the component
                    inventory was regenerated, which is a software fact. Calling it
                    a review would imply a compliance audit took place. */}
                Last updated {data.generated_date}
              </Typography>
            )}
          </Box>

          {failed && (
            <Alert severity="info">
              The component list could not be loaded. The principal modelling
              licence is reproduced below; attribution information is available
              upon request from MetEn Solutions.
            </Alert>
          )}

          {/* Guaranteed baseline if the inventory fails: pvlib's BSD-3-Clause
              notice must be reproduced regardless, so it is rendered from the
              baked-in text rather than lost with the fetch. */}
          {failed && (
            <Box sx={{ ...MONO, p: 1.5, bgcolor: "grey.50", border: "1px solid", borderColor: "divider", borderRadius: 1, maxHeight: 320, overflow: "auto" }}>
              {PVLIB_LICENSE}
            </Box>
          )}

          <Divider />

          {/* Core modelling components, named deliberately. */}
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Core Scientific &amp; Optimisation Libraries
            </Typography>
            {/* No comparison against our own implementations. "rather than in-house
                approximations" implied PYTHIA holds no proprietary modelling of its
                own, cast any such work as mere approximation, and said more about
                how the product is built than a notices page needs to. "Peer-reviewed"
                also went: defensible for pvlib and PySAM, not for every row here. */}
            <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
              PYTHIA's physical and market modelling capabilities incorporate
              established open-source scientific and optimisation libraries. This
              supports traceability to published, independently validated
              methodologies.
            </Typography>
            {data ? (
              <Box sx={{ mt: 1.5 }}>
                <ComponentTable rows={coreRows} withNotes onShowLicence={setLicencePkg} />
              </Box>
            ) : (
              !failed && <CircularProgress size={18} sx={{ mt: 2 }} />
            )}
          </Box>

          <Divider />

          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Application Libraries
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
              The principal libraries the PYTHIA application and services are built
              with. Each is used under its own licence, available via the links
              below.
            </Typography>
            {data && (
              <Box sx={{ mt: 1.5 }}>
                <ComponentTable rows={appRows} onShowLicence={setLicencePkg} />
              </Box>
            )}
          </Box>

          <Divider />

          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Server Components
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
              {data?.notes?.system ||
                "Operating-system packages in the PYTHIA server image. These run on our servers and are not distributed to users."}
            </Typography>
            {data && (
              <Table size="small" sx={{ mt: 1.5 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Component</TableCell>
                    <TableCell>Licence</TableCell>
                    <TableCell>Role</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data.system || []).map((p) => (
                    <TableRow key={p.name} hover>
                      <TableCell translate="no" data-no-translate sx={{ fontWeight: 600 }}>
                        {p.url ? (
                          <Link href={p.url} target="_blank" rel="noreferrer noopener">
                            {p.name}
                          </Link>
                        ) : (
                          p.name
                        )}
                      </TableCell>
                      <TableCell>{p.license}</TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {p.role}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Clarification
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
              PYTHIA is an independent commercial platform developed by MetEn Solutions.
              Each component above is used under its own licence. Inclusion here does not
              imply that the authors or organisations concerned endorse PYTHIA, MetEn
              Solutions, or any result the platform produces.
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.75 }}>
              Questions regarding open-source compliance or attribution may be
              directed to MetEn Solutions.
            </Typography>
            {/* Trademark line only. The non-endorsement point (BSD-3 clause 3 and
                its equivalents) is already made in the first paragraph, in a
                stronger form that also covers the results the platform produces —
                stating it twice read like two templates spliced together. */}
            <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.75 }}>
              All trademarks remain the property of their respective owners.
            </Typography>
          </Box>

          {/* The complete attribution notices for the browser application.
              This is the file that actually discharges the attribution
              obligation: the bundle is distributed to every visitor, and MIT,
              BSD and ISC each require their copyright notice to accompany a
              distribution. The curated tables above name the principal
              components for a reader assessing the platform; they do not
              reproduce notices for the full bundled tree, and were never
              intended to.

              Plain text, opened in a new tab, deliberately: an auditor can
              archive and diff it, it needs no JavaScript, and it survives any
              restyling of this page. Server-side components are NOT in it -
              they are not distributed, so no notice has to travel with them. */}
          <Divider />

          <Box>
            <Link
              href={ATTRIBUTION_URL}
              target="_blank"
              rel="noreferrer noopener"
              variant="caption"
              sx={{ color: "text.secondary" }}
            >
              Complete Frontend Attribution Notices
            </Link>
          </Box>
        </Stack>
      </Paper>

      <LicenceDialog pkg={licencePkg} onClose={() => setLicencePkg(null)} />
    </Box>
  );
}
